import type * as React from 'react'

import { Button, CoachMark } from '@buffer-mono/popcorn'

import { useBulkActionsMenuContext } from './BulkActionsMenu/BulkActionsMenuContext'
import type { BulkActionsEntity } from './BulkActionsMenu/BulkActionsMenuContext'

import type { JSX } from 'react'

export type CoachmarkType = 'bannerDismissed' | 'resolveComments'

type CoachmarkConfig = {
  title: string
  description: string
  footerType: 'gotIt' | 'maybeLaterAndResolve'
  // Label for the primary "resolve all" button (maybeLaterAndResolve footer only).
  resolveButtonLabel: string
}

// Copy is parametrized by entity so Comments and Mentions stay consistent. The
// comments strings are unchanged from the original comments-only coach mark.
const COACHMARK_CONFIGS: Record<
  BulkActionsEntity,
  Record<CoachmarkType, CoachmarkConfig>
> = {
  comments: {
    bannerDismissed: {
      title: 'Resolve all comments any time',
      description: 'You can resolve comments using this menu.',
      footerType: 'gotIt',
      resolveButtonLabel: 'Resolve all comments',
    },
    resolveComments: {
      title: 'Resolve all comments',
      description:
        'Doing a little clean up? You can resolve all the new comments in one click.',
      footerType: 'maybeLaterAndResolve',
      resolveButtonLabel: 'Resolve all comments',
    },
  },
  mentions: {
    bannerDismissed: {
      title: 'Resolve all mentions any time',
      description: 'You can resolve mentions using this menu.',
      footerType: 'gotIt',
      resolveButtonLabel: 'Resolve all mentions',
    },
    resolveComments: {
      title: 'Resolve all mentions',
      description:
        'Doing a little clean up? You can resolve all the new mentions in one click.',
      footerType: 'maybeLaterAndResolve',
      resolveButtonLabel: 'Resolve all mentions',
    },
  },
}

export type BulkActionsCoachmarkProps = {
  type: CoachmarkType | null
  children: React.ReactNode
  onDismiss: () => void
}

export function BulkActionsCoachmark({
  type,
  children,
  onDismiss,
}: BulkActionsCoachmarkProps): JSX.Element {
  const { entity, requestResolve } = useBulkActionsMenuContext()
  if (type === null) {
    return <>{children}</>
  }

  const config = COACHMARK_CONFIGS[entity][type]

  const style: React.CSSProperties & Record<`--${string}`, string> = {
    '--coach-mark-max-width': '350px',
  }

  return (
    <CoachMark defaultOpen={true} onDismiss={onDismiss} dismissTimeout={null}>
      <CoachMark.Overlay>
        <CoachMark.Spotlight size="medium" />
        {children}
      </CoachMark.Overlay>
      <CoachMark.Content style={style} showCloseIcon={false}>
        <CoachMark.Title>{config.title}</CoachMark.Title>
        <CoachMark.Description>{config.description}</CoachMark.Description>
        <CoachMark.Footer>
          {config.footerType === 'gotIt' ? (
            <CoachMark.Dismiss>
              <Button variant="primary">Got It</Button>
            </CoachMark.Dismiss>
          ) : (
            <>
              <Button variant="tertiary" onClick={onDismiss}>
                Maybe Later
              </Button>
              <Button
                variant="primary"
                onClick={(): void => {
                  onDismiss()
                  requestResolve('all')
                }}
              >
                {config.resolveButtonLabel}
              </Button>
            </>
          )}
        </CoachMark.Footer>
      </CoachMark.Content>
    </CoachMark>
  )
}
